import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "./_roadsCommon";

/** Ch 21.3 — Cross-Section Template Editor. */
export default function CrossSectionEditorPanel({ projectId }) {
  const [tables, setTables] = useState({ components: [] });
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [pendingComp, setPendingComp] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { (async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/projects/${projectId}/roads/design-tables`),
        api.get(`/projects/${projectId}/roads/cross-section-templates`),
      ]);
      setTables(r1.data); setTemplates(r2.data.templates || []);
      if (r2.data.templates?.[0]) setActive(r2.data.templates[0]);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  })(); /* eslint-disable-next-line */ }, []);

  const createNew = async () => {
    const name = window.prompt("Template name:", `XS-${templates.length + 1}`);
    if (!name) return;
    const def = [
      { id: "shoulder-paved", width_m: 1.5 },
      { id: "carriageway-lane", width_m: 3.5 },
      { id: "carriageway-lane", width_m: 3.5 },
      { id: "median-raised", width_m: 1.5 },
      { id: "carriageway-lane", width_m: 3.5 },
      { id: "carriageway-lane", width_m: 3.5 },
      { id: "shoulder-paved", width_m: 1.5 },
    ];
    try {
      const { data } = await api.post(
        `/projects/${projectId}/roads/cross-section-templates`,
        { name, components: def });
      setActive(data.template);
      setTemplates([...templates, data.template]);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const saveTemplate = async (comps) => {
    if (!active) return;
    try {
      const { data } = await api.put(
        `/projects/${projectId}/roads/cross-section-templates/${active.id}`,
        { name: active.name, components: comps });
      setActive({ ...active, components: comps, total_width_m: data.total_width_m });
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const addComp = (palC) => {
    const newComp = { id: palC.id,
                       width_m: palC.default_width_m || 0,
                       slope: palC.default_slope || 0,
                       thickness_mm: palC.default_thickness_mm,
                       category: palC.category };
    saveTemplate([...(active?.components || []), newComp]);
    setPendingComp(null);
  };

  const editComp = (idx, key, val) => {
    const c = active.components.map((x, i) =>
      i === idx ? { ...x, [key]: parseFloat(val) || 0 } : x);
    setActive({ ...active, components: c });
  };

  const delComp = (idx) => {
    saveTemplate(active.components.filter((_, i) => i !== idx));
  };

  const totalWidth = (active?.components || [])
    .reduce((acc, c) => acc + (parseFloat(c.width_m) || 0), 0);
  const componentLabel = (id) =>
    tables.components.find((c) => c.id === id)?.label || id;

  return (
    <div data-testid="crosssection-editor" style={{ display: "grid",
            gridTemplateColumns: "260px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* Component palette */}
      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={{ ...label, marginBottom: 4 }}>Component palette</div>
        {(tables.components || []).map((c) => (
          <button key={c.id} data-testid={`xs-pal-${c.id}`}
            style={{ ...btnGhost, width: "100%", padding: "4px 6px", textAlign: "left",
                      fontSize: 10, marginBottom: 2 }}
            onClick={() => addComp(c)}>{c.label}</button>
        ))}
      </div>

      {/* Canvas — sectional view */}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 6, padding: 6, background: "var(--pf-surface-2)",
                       borderBottom: "1px solid var(--pf-border)", alignItems: "center" }}>
          {!templates.length ? (
            <button data-testid="xs-new" style={btn} onClick={createNew}>+ New template</button>
          ) : (
            <select data-testid="xs-select" style={inputStyle}
              value={active?.id || ""} onChange={(e) =>
                setActive(templates.find((t) => t.id === e.target.value))}>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <button data-testid="xs-new-2" style={btnGhost} onClick={createNew}>+</button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, fontFamily: "monospace", color: "#F26B1F" }}>
            Total width: {totalWidth.toFixed(2)} m
          </span>
        </div>
        {active && (
          <div data-testid="xs-canvas"
                style={{ padding: 16, height: "calc(100% - 32px)", overflowY: "auto",
                         background: "#0a1220" }}>
            <div style={{ display: "flex", border: "2px solid #F26B1F",
                           background: "#1a2845", height: 80 }}>
              {(active.components || []).map((c, i) => {
                const w = (parseFloat(c.width_m) || 0) / Math.max(totalWidth, 1) * 100;
                const color = c.category === "pavement" ? "#3aa0ff" :
                                c.category === "median" ? "#888" :
                                c.category === "drain" ? "#1f5fa8" :
                                c.category === "earthwork" ? "#7a4f1b" :
                                c.category === "non-vehicular" ? "#22c55e" :
                                "#444";
                return (
                  <div key={i} data-testid={`xs-comp-${i}`}
                        style={{ width: `${w}%`, background: color,
                                  borderLeft: i > 0 ? "1px solid #fff" : "none",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  color: "#fff", fontSize: 10, fontFamily: "monospace" }}>
                    {c.width_m}m
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 16, fontSize: 10, color: "#aaa" }}>
              Sweep this template along the alignment + profile to generate the 3-D corridor.
            </div>
          </div>
        )}
      </div>

      {/* Right — component list */}
      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Components ({active?.components?.length || 0})</div>
        {(active?.components || []).map((c, i) => (
          <div key={i} data-testid={`xs-row-${i}`} style={{ padding: 4, marginTop: 4,
                background: "rgba(255,255,255,0.04)", borderRadius: 4 }}>
            <div style={{ fontSize: 11, color: "#fff" }}>{componentLabel(c.id)}</div>
            <div style={{ display: "flex", gap: 4, alignItems: "center", marginTop: 3 }}>
              <span style={{ fontSize: 9, color: "#888" }}>w</span>
              <input style={{ ...inputStyle, padding: 1, fontSize: 10, width: 50 }} type="number" step="0.1"
                        value={c.width_m || ""} onChange={(e) => editComp(i, "width_m", e.target.value)}
                        onBlur={() => saveTemplate(active.components)} />
              {c.thickness_mm !== undefined && (
                <>
                  <span style={{ fontSize: 9, color: "#888" }}>t</span>
                  <input style={{ ...inputStyle, padding: 1, fontSize: 10, width: 50 }} type="number"
                            value={c.thickness_mm || ""}
                            onChange={(e) => editComp(i, "thickness_mm", e.target.value)}
                            onBlur={() => saveTemplate(active.components)} />
                  <span style={{ fontSize: 9, color: "#888" }}>mm</span>
                </>
              )}
              <button style={{ ...btnGhost, padding: "1px 5px", fontSize: 10, marginLeft: "auto" }}
                        onClick={() => delComp(i)}>×</button>
            </div>
          </div>
        ))}
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}
